/**
 * Operator copy for the probe levels (#2658 follow-up): the one-line summary
 * shown on the SKU row and the leg card, the level flow's arrow captions, and
 * each drawer section's computed effect. Pure: values + translator in, text
 * out; the numbers come from probeEffects.
 */
import type { I18nKey, TParams } from '../../i18n/index.ts';
import {
  alertCadenceSeconds,
  alertTrip,
  compactDuration,
  cooldownSequence,
  minutesText,
  normalTrip,
  qualityBadShotsToTrip,
  qualityRecoverShots,
  qualityShotsInWindow,
  warEffect,
  warTriggerFailures,
} from './probeEffects.ts';
import type { ProbeSection, ProbeValues } from './probeSettingsModel.ts';

type Translate = (key: I18nKey, params?: TParams) => string;

function durationText(seconds: number, t: Translate): string {
  const compact = compactDuration(seconds);
  return compact.unit === 'minutes'
    ? t('guaranteed.probe_summary.minutes', { n: compact.value })
    : t('guaranteed.probe_summary.seconds', { n: compact.value });
}

/** 「常规 15 分 · 警戒 2 分 · 战争 4×10 秒 · 质量 120 分 15%」 */
export function probeSummary(values: ProbeValues, t: Translate): string {
  return [
    t('guaranteed.probe_summary.normal', { every: durationText(values.probe_interval_minutes * 60, t) }),
    t('guaranteed.probe_summary.alert', { every: durationText(values.probe_alert_interval_seconds, t) }),
    t('guaranteed.probe_summary.war', { shots: values.probe_burst_wave_shots, every: values.probe_burst_wave_every_seconds }),
    t('guaranteed.probe_summary.quality', { window: values.probe_quality_window_minutes, pct: values.probe_quality_trip_pct }),
  ].join(' · ');
}

export interface LevelFlowCopy {
  readonly normalCadence: string;
  readonly warCadence: string;
  readonly alertCadence: string;
  readonly toWar: string;
  readonly toAlert: string;
  readonly toNormal: string;
  readonly cooldown: string;
}

/** Captions of 常规 → 战争 → 警戒 → 常规. */
export function levelFlowCopy(values: ProbeValues, t: Translate): LevelFlowCopy {
  const trigger = warTriggerFailures(values);
  return {
    normalCadence: t('guaranteed.probe_flow.every', { every: durationText(values.probe_interval_minutes * 60, t) }),
    warCadence: t('guaranteed.probe_flow.war_cadence', {
      shots: values.probe_burst_wave_shots,
      every: values.probe_burst_wave_every_seconds,
    }),
    alertCadence: t('guaranteed.probe_flow.every', { every: durationText(alertCadenceSeconds(values), t) }),
    toWar: t('guaranteed.probe_flow.to_war', {
      window: trigger.windowSeconds,
      first: trigger.first,
      fallback: trigger.fallback,
      pct: trigger.pct,
      fast_window: trigger.fast.windowSeconds,
      fast_min: trigger.fast.failures,
      fast_pct: trigger.fast.pct,
    }),
    toAlert: t('guaranteed.probe_flow.to_alert', {
      waves: values.probe_burst_exit_good_waves,
      max: durationText(values.probe_burst_max_seconds, t),
    }),
    toNormal: t('guaranteed.probe_flow.to_normal', { calm: values.probe_alert_calm_minutes }),
    cooldown: t('guaranteed.probe_flow.cooldown', { base: values.probe_war_cooldown_base_minutes }),
  };
}

/** The computed effect printed under each section title. */
export function sectionEffect(section: ProbeSection, values: ProbeValues, t: Translate): string {
  switch (section) {
    case 'normal': {
      const trip = normalTrip(values);
      return t('guaranteed.probe_effect.normal', {
        fast: minutesText(trip.fastestSeconds),
        slow: minutesText(trip.slowestSeconds),
        rounds: values.probe_trip_failed_rounds,
      });
    }
    case 'quality': {
      const window = qualityShotsInWindow(values);
      const recover = t('guaranteed.probe_effect.quality_recover', {
        recover_window: values.probe_quality_recover_window_minutes,
        pct: values.probe_quality_recover_pct,
        shots: qualityRecoverShots(values),
        rounds: values.probe_recover_ok_rounds,
      });
      return window.judged
        ? t('guaranteed.probe_effect.quality', {
          shots: window.shots,
          bad: qualityBadShotsToTrip(values, window.shots),
          window: values.probe_quality_window_minutes,
        }) + recover
        : t('guaranteed.probe_effect.quality_unjudged', {
          shots: window.shots,
          min: values.probe_quality_min_shots,
          window: values.probe_quality_window_minutes,
        });
    }
    case 'alert': {
      const trip = alertTrip(values);
      const late = warTriggerFailures(values).firstByteLateSeconds;
      return t('guaranteed.probe_effect.alert', {
        every: durationText(alertCadenceSeconds(values), t),
        fast: minutesText(trip.fastestSeconds),
        slow: minutesText(trip.slowestSeconds),
        calm: values.probe_alert_calm_minutes,
      }) + (late === null
        ? t('guaranteed.probe_effect.alert_late_off')
        : t('guaranteed.probe_effect.alert_late_on', { seconds: late }));
    }
    case 'war': {
      const war = warEffect(values);
      const trip = war.sustainSeconds === 0
        ? t('guaranteed.probe_effect.war_one_wave', { fast: war.fastestSeconds, slow: war.slowestSeconds })
        : t('guaranteed.probe_effect.war', {
          fast: war.fastestSeconds,
          slow: war.slowestSeconds,
          sustain: war.sustainSeconds,
        });
      return `${trip}${t('guaranteed.probe_effect.war_end', {
        exit: war.exitSeconds,
        extra: war.verdictWavesPastMax,
        max: durationText(war.maxSeconds, t),
        shots: war.maxShots,
      })}`;
    }
    case 'cooldown':
      return t('guaranteed.probe_effect.cooldown', {
        sequence: cooldownSequence(values).join(' → '),
        reset: values.probe_war_backoff_reset_minutes,
      });
  }
}
